import { useEffect, useRef, useState } from "react";
import GridLayout from "react-grid-layout";

import IconsEnum from "../classes/enums/IconsEnum";
import Button from "../components/button/Button";
import IconButton from "../components/iconButton/IconButton";
import LangSwitcher from "../components/langSwitcher/LangSwitcher";
import NavBar from "../components/navBar/NavBar";
import NavBarButton from "../components/navBarButton/NavBarButton";
import RangeSlider from "../components/rangeSlider/RangeSlider";
import Select from "../components/select/Select";
import SideBar from "../components/sideBar/SideBar";
import TextBox from "../components/textBox/TextBox";
import ToggleThemeButton from "../components/toggleThemeButton/ToggleThemeButton";
import "./StyleGuide.css";

import vtkWSLinkClient from "@kitware/vtk.js/IO/Core/WSLinkClient";
import vtkRemoteView from "@kitware/vtk.js/Rendering/Misc/RemoteView";
import SmartConnect from "wslink/src/SmartConnect";

import { useAppContext } from "../classes/contexts/AppContext";
import ToggleValidationButton from "../components/toggleValidationButton/ToggleValidationButton";

const StyleGuide = () => {
  const divPlaceholder = useRef(null);
  const divPlaceholderDraggable = useRef(null);
  const [demoText, setDemoText] = useState("");

  const { showPopup } = useAppContext();

  const selectItems = ["Option 1", "Option 2", "Option 3"];
  useEffect(() => {
    document.title = "Style guide";
  }, []);

  const openPopup = () => {
    showPopup(<DemoPopup />);
  };

  const startRemote = () => {
    vtkWSLinkClient.setSmartConnectClass(SmartConnect);

    const clientToConnect = vtkWSLinkClient.newInstance();

    // Error
    clientToConnect.onConnectionError(httpReq => {
      const message =
        (httpReq && httpReq.response && httpReq.response.error) ||
        `Connection error`;
    });

    // Close
    clientToConnect.onConnectionClose(httpReq => {
      const message =
        (httpReq && httpReq.response && httpReq.response.error) ||
        `Connection close`;
    });

    // hint: if you use the launcher.py and ws-proxy just leave out sessionURL
    // (it will be provided by the launcher)
    const config = {
      application: "cone",
      sessionURL: "wss://localhost:8787/ws",
    };

    // Connect
    clientToConnect
      .connect(config)
      .then(validClient => {
        const viewStream = clientToConnect
          .getImageStream()
          .createViewStream("2");
        const session = validClient.getConnection().getSession();
        const view = vtkRemoteView.newInstance({
          rpcWheelEvent: "viewport.mouse.zoom.wheel",
          viewStream,
        });
        view.setSession(session);
        view.setContainer(divPlaceholder.current);
        view.setInteractiveRatio(0.7); // the scaled image compared to the clients view resolution
        view.setInteractiveQuality(50); // jpeg quality

        window.addEventListener("resize", view.resize);

        const view2 = vtkRemoteView.newInstance({
          rpcWheelEvent: "viewport.mouse.zoom.wheel",
          viewStream,
        });
        view2.setSession(session);
        view2.setContainer(divPlaceholderDraggable.current);
        view2.setInteractiveRatio(0.7); // the scaled image compared to the clients view resolution
        view2.setInteractiveQuality(50); // jpeg quality

        window.addEventListener("resize", view2.resize);
      })
      .catch(error => {});
  };

  return (
    <div>
      <div>
        <h1>Colors</h1>
        <div className="colorCircles">
          <div className="circle fontColor"></div>
          <div className="circle iconColor"></div>
          <div className="circle selectedColor"></div>
          <div className="circle highlightColor"></div>
          <div className="circle buttonColor"></div>
          <div className="circle rowColor"></div>
          <div className="circle functionColor"></div>
          <div className="circle headerColor"></div>
          <div className="circle backgroundBoxColor"></div>
          <div className="circle backgroundColor"></div>
          <div className="circle highlightedFontColor"></div>
          <div className="circle errorColor"></div>
        </div>
      </div>

      <div className="typography">
        <h1>Typography</h1>
        <span className="App-title-windows">Main Title</span>
        <span className="App-title-widgets">Widgets Title</span>
        <span> Basic Text</span>
        <span className="App-text-medium">Details</span>
      </div>

      <div>
        <h1>Controls</h1>
        <div className="controls-array">
          <div className="control-item">
            <div className="control-text">
              <span className="App-title-widgets">Change theme switch:</span>
            </div>
            <div className="control-sample">
              <ToggleThemeButton />
            </div>
          </div>

          <div className="control-item">
            <div className="control-text">
              <span className="App-title-widgets">Generic Toggle:</span>
            </div>
            <div className="control-sample">
              <ToggleValidationButton />
            </div>
          </div>

          <div className="control-item">
            <div className="control-text">
              <span className="App-title-widgets">Select language:</span>
            </div>
            <div className="control-sample">
              <LangSwitcher />
            </div>
          </div>

          <div className="control-item">
            <div className="control-text">
              <span className="App-title-widgets">Nav Bar button:</span>
            </div>
            <div className="control-sample">
              <NavBarButton value={"Navigation button"} />
            </div>
          </div>

          <div className="control-item">
            <div className="control-text">
              <span className="App-title-widgets">Button:</span>
            </div>
            <div className="control-sample">
              <Button value="Button" />
            </div>
          </div>

          <div className="control-item">
            <div className="control-text">
              <span className="App-title-widgets">Icon Button:</span>
            </div>
            <div className="control-sample control-icon-button">
              <IconButton icon={IconsEnum.SHIELDCHECKED} />
              <IconButton icon={IconsEnum.CAMERA} />
              <IconButton icon={IconsEnum.SHIELDCHECKED} isCircle={false} />
              <IconButton icon={IconsEnum.SHIELDCHECKED} isLightBlue={false} />
            </div>
          </div>

          <div className="control-item">
            <div className="control-text">
              <span className="App-title-widgets">Text Box:</span>
            </div>
            <div className="control-sample">
              <Select items={selectItems} />
            </div>
          </div>

          <div className="control-item">
            <div className="control-text">
              <span className="App-title-widgets">Text Box:</span>
            </div>
            <div className="control-sample">
              <TextBox
                placeholder="Text"
                value={demoText}
                onChange={e => setDemoText(e.target.value)}
              />
            </div>
          </div>

          <div className="control-item">
            <div className="control-text">
              <span className="App-title-widgets">Slider:</span>
            </div>
            <div className="control-sample">
              <RangeSlider
                min={0}
                max={100}
                rangeValue={50}
                onRangeValueChanged={() => {}}
              />
            </div>
          </div>

          <div className="control-item">
            <div className="control-text">
              <span className="App-title-widgets">PopUp:</span>
            </div>
            <div className="control-sample">
              <Button onClick={openPopup} value="Open PopUp" />
            </div>
          </div>

          <div className="control-item" style={{ height: "500px" }}>
            <div className="control-text">
              <span className="App-title-widgets">SideBar:</span>
            </div>
            <div className="control-sample">
              <SideBar />
            </div>
          </div>

          <div className="control-item">
            <div className="control-text">
              <span className="App-title-widgets">Remote view:</span>
              <Button onClick={startRemote} value="Display cone" />
            </div>
            <div className="control-sample">
              <div ref={divPlaceholder} className="remoteView"></div>
            </div>
          </div>

          <div className="control-item">
            <div className="control-text">
              <span className="App-title-widgets">Docking:</span>
            </div>
            <div className="control-sample">
              <GridLayout
                className="layout grid-layout"
                cols={12}
                rowHeight={30}
                width={1200}
              >
                <div
                  key="a"
                  data-grid={{ x: 0, y: 0, w: 1, h: 2, static: true }}
                >
                  a
                </div>
                <div
                  key="b"
                  data-grid={{ x: 1, y: 0, w: 3, h: 2, minW: 2, maxW: 4 }}
                >
                  b
                </div>
                <div key="c" data-grid={{ x: 4, y: 0, w: 4, h: 4 }}>
                  <div
                    ref={divPlaceholderDraggable}
                    className="remoteView"
                  ></div>
                  <hr />
                  <hr />
                  <span className="react-grid-dragHandleExample">
                    [DRAG HERE]
                  </span>
                  <hr />
                  <hr />
                </div>
              </GridLayout>
            </div>
          </div>

          <div className="control-item">
            <div className="control-text">
              <span className="App-title-widgets">
                Navigation bar and Action bar:
              </span>
            </div>
            <div className="control-sample">
              <NavBar />
              <div className="test"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StyleGuide;

const DemoPopup = () => {
  const { hidePopup } = useAppContext();

  const handleClose = () => {
    hidePopup();
  };

  return (
    <div className="App-popup-demo">
      <button onClick={handleClose}>Close</button>
    </div>
  );
};
